import { Link, usePage } from '@inertiajs/react';

import ProductImageFlipCard from './ProductImageFlipCard';
import { CardmarketProductLink } from '../../components/shared/CardmarketProductLink';
import { WishlistButton } from '../../components/shared/WishlistButton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Routes } from '../../models/routes';
import {
    CurrentUser,
    Offer,
    OverviewProduct,
    ProductVersionsSummary,
} from '../../types';

type PageProps = {
    currentUser: Partial<CurrentUser>;
};

interface Props {
    product: OverviewProduct;
    offers: Offer[];
    productVersions: ProductVersionsSummary | null;
}

export default function ProductOverview({
    product,
    offers = [],
    productVersions,
}: Props) {
    const { currentUser } = usePage<PageProps>().props;

    return (
        <div className="mb-6 grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-12">
            <div className="lg:col-span-3">
                <div className="sticky top-24">
                    <ProductImageFlipCard
                        key={product.id}
                        image={product.image}
                        secondaryImage={product.secondary_image}
                        imageAlt={product.image_alt}
                    />
                </div>
            </div>

            <div className="lg:col-span-5">
                <div>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-bold sm:text-2xl">
                            {product.name}
                        </h1>
                        <Badge className="text-xs">
                            {product.expansion_tag}
                        </Badge>
                        <CardmarketProductLink href={product.cardmarket_url} />
                        {productVersions && (
                            <Button
                                asChild
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                            >
                                <Link
                                    href={productVersions.urlPath}
                                    data-testid="product-versions-link"
                                >
                                    Інші версії ({productVersions.count})
                                </Link>
                            </Button>
                        )}
                    </div>
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>
                            {offers?.length
                                ? 'В наявності'
                                : 'Немає в наявності'}
                        </span>
                    </div>
                    {product.min_price && (
                        <p className="mb-2 text-lg font-bold sm:text-xl">
                            Від ₴{product.min_price.toFixed(2)}
                        </p>
                    )}

                    <div className="mb-3 rounded-lg bg-muted/30">
                        <div className="text-s grid grid-cols-1 gap-x-4 gap-y-1">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">
                                    TCG:
                                </span>
                                <span className="font-medium">
                                    {product.universe_name}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">
                                    Колекція:
                                </span>
                                <span className="font-medium">
                                    {product.expansion_name}
                                </span>
                            </div>

                            {product.property_set &&
                                Object.entries(
                                    product.processed_property_set,
                                ).map((pair) => {
                                    if (!pair[1]) return null;
                                    return (
                                        <div
                                            key={pair[0]}
                                            className="flex justify-between"
                                        >
                                            <span className="text-muted-foreground">
                                                {pair[0]}
                                            </span>
                                            <span className="font-medium">
                                                {pair[1]}
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-start md:justify-start lg:col-span-4 lg:justify-end">
                <div className="flex w-full flex-col gap-2 sm:w-[240px]">
                    <WishlistButton
                        productId={product.id}
                        initialWishlisted={product.wishlisted}
                        showLabel
                        className="h-9 w-full text-sm"
                    />
                    <Link
                        className="w-full"
                        href={
                            currentUser
                                ? `/profile/offers/new?product_id=${product.id}`
                                : Routes.SignIn
                        }
                    >
                        <Button className="h-9 w-full text-sm">
                            Створити пропозицію
                        </Button>
                    </Link>
                    <Button
                        asChild
                        variant="outline"
                        className="h-9 w-full text-sm"
                    >
                        <a href="https://carton.cards/contact">
                            Звʼязатися з підтримкою
                        </a>
                    </Button>
                </div>
            </div>
        </div>
    );
}
